// Custom hook for Minew data fetching and management

import { useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  ROOMS_QUERY,
  GATEWAYS_QUERY,
  BRACELETS_QUERY,
  PHYSICIANS_WITH_BRACELET_QUERY,
  PROCEDURE_TYPES_QUERY,
  OPERATION_ROOMS_QUERY,
  PATIENTS_QUERY,
  STAFF_QUERY,
  ANESTHESIOLOGISTS_QUERY,
  EDIT_GATEWAY,
  CREATE_PATIENT,
  EDIT_PATIENT,
  EDIT_STAFF,
  EDIT_PHYSICIAN,
  EDIT_ANESTHESIOLOGIST,
} from '../graphql';
import { TEST_BEACONS } from '../constants';
import type {
  Room,
  Gateway,
  Bracelet,
  Patient,
  Physician,
  Staff,
  Anesthesiologist,
  ProcedureType,
  RoomsQueryData,
  GatewaysQueryData,
  BraceletsQueryData,
  PhysiciansQueryData,
  ProcedureTypesQueryData,
  OperationRoomsQueryData,
  PatientsQueryData,
  RoomsByType,
  ExistingTestGateways,
  BeaconToPatientMap,
} from '../types';

interface StaffQueryData {
  staff: Staff[];
}

interface AnesthesiologistsQueryData {
  anesthesiologists: Anesthesiologist[];
}

// Using 'any' for Apollo mutation function types due to complex generic constraints
type MutationFunction = (options?: any) => Promise<any>;

export interface UseMinewDataReturn {
  // Data
  rooms: Room[];
  existingGateways: Gateway[];
  existingBracelets: Bracelet[];
  physicians: Physician[];
  procedureTypes: ProcedureType[];
  operationRooms: Room[];
  patients: Patient[];
  staff: Staff[];
  anesthesiologists: Anesthesiologist[];

  // Computed data
  roomsByType: RoomsByType;
  existingTestGateways: ExistingTestGateways;
  existingTestBracelets: Set<string>;
  patientsWithTestBracelets: Patient[];
  beaconToPatientMap: BeaconToPatientMap;
  beaconToStaffMap: Record<string, Staff>;
  beaconToPhysicianMap: Record<string, Physician>;
  beaconToAnesthesiologistMap: Record<string, Anesthesiologist>;

  // Loading states
  isLoading: boolean;

  // Mutations
  editGateway: MutationFunction;
  createPatient: MutationFunction;
  editPatient: MutationFunction;
  editStaff: MutationFunction;
  editPhysician: MutationFunction;
  editAnesthesiologist: MutationFunction;

  // Refetch functions
  refetchGateways: () => void;
  refetchPatients: () => void;
  refetchStaff: () => void;
  refetchPhysicians: () => void;
  refetchAnesthesiologists: () => void;
}

export const useMinewData = (): UseMinewDataReturn => {
  // Queries
  const { data: roomsData, loading: roomsLoading } = useQuery<RoomsQueryData>(ROOMS_QUERY);
  const {
    data: gatewaysData,
    loading: gatewaysLoading,
    refetch: refetchGateways,
  } = useQuery<GatewaysQueryData>(GATEWAYS_QUERY);
  const { data: braceletsData } = useQuery<BraceletsQueryData>(BRACELETS_QUERY);
  const { data: physiciansData, refetch: refetchPhysicians } =
    useQuery<PhysiciansQueryData>(PHYSICIANS_WITH_BRACELET_QUERY);
  const { data: procedureTypesData } = useQuery<ProcedureTypesQueryData>(PROCEDURE_TYPES_QUERY);
  const { data: operationRoomsData } = useQuery<OperationRoomsQueryData>(OPERATION_ROOMS_QUERY);
  const { data: patientsData, refetch: refetchPatients } = useQuery<PatientsQueryData>(PATIENTS_QUERY);
  const { data: staffData, refetch: refetchStaff } = useQuery<StaffQueryData>(STAFF_QUERY);
  const { data: anesthesiologistsData, refetch: refetchAnesthesiologists } =
    useQuery<AnesthesiologistsQueryData>(ANESTHESIOLOGISTS_QUERY);

  // Mutations
  const [editGateway] = useMutation(EDIT_GATEWAY);
  const [createPatient] = useMutation(CREATE_PATIENT);
  const [editPatient] = useMutation(EDIT_PATIENT);
  const [editStaff] = useMutation(EDIT_STAFF);
  const [editPhysician] = useMutation(EDIT_PHYSICIAN);
  const [editAnesthesiologist] = useMutation(EDIT_ANESTHESIOLOGIST);

  // Memoize data arrays
  const rooms = useMemo(() => roomsData?.rooms || [], [roomsData?.rooms]);
  const existingGateways = useMemo(() => gatewaysData?.gateways || [], [gatewaysData?.gateways]);
  const existingBracelets = useMemo(() => braceletsData?.bracelets || [], [braceletsData?.bracelets]);
  const physicians = useMemo(() => physiciansData?.physicians || [], [physiciansData?.physicians]);
  const procedureTypes = useMemo(() => procedureTypesData?.procedureTypes || [], [procedureTypesData?.procedureTypes]);
  const operationRooms = useMemo(() => operationRoomsData?.rooms || [], [operationRoomsData?.rooms]);
  const patients = useMemo(() => patientsData?.patients || [], [patientsData?.patients]);
  const staff = useMemo(() => staffData?.staff || [], [staffData?.staff]);
  const anesthesiologists = useMemo(
    () => anesthesiologistsData?.anesthesiologists || [],
    [anesthesiologistsData?.anesthesiologists]
  );

  // Group rooms by type
  const roomsByType = useMemo(() => {
    const grouped: RoomsByType = {};
    rooms.forEach(room => {
      if (!grouped[room.type]) grouped[room.type] = [];
      grouped[room.type].push(room);
    });
    return grouped;
  }, [rooms]);

  // Check which test gateways already exist
  const existingTestGateways = useMemo(() => {
    const map: ExistingTestGateways = {};
    existingGateways.forEach(gw => {
      if (gw.id.startsWith('GW_TEST_')) {
        map[gw.id] = gw;
      }
    });
    return map;
  }, [existingGateways]);

  // Check which test bracelets already exist
  const testBeaconIds = useMemo(() => new Set(TEST_BEACONS.map(b => b.id)), []);
  const existingTestBracelets = useMemo(() => {
    const set = new Set<string>();
    existingBracelets.forEach(b => {
      if (testBeaconIds.has(b.id)) {
        set.add(b.id);
      }
    });
    return set;
  }, [existingBracelets, testBeaconIds]);

  // Find patients that have test bracelets assigned
  const patientsWithTestBracelets = useMemo(
    () => patients.filter(p => p.braceletId && testBeaconIds.has(p.braceletId)),
    [patients, testBeaconIds]
  );

  // Map beacon IDs to their assigned patients
  const beaconToPatientMap = useMemo(() => {
    const map: BeaconToPatientMap = {};
    patients.forEach(p => {
      if (p.braceletId) {
        map[p.braceletId] = p;
      }
    });
    return map;
  }, [patients]);

  // Map beacon IDs to their assigned staff (by bracelet.id)
  const beaconToStaffMap = useMemo(() => {
    const map: Record<string, Staff> = {};
    staff.forEach(s => {
      if (s.bracelet?.id) {
        map[s.bracelet.id] = s;
      }
    });
    return map;
  }, [staff]);

  // Map beacon IDs to their assigned physicians (by bracelet.id)
  const beaconToPhysicianMap = useMemo(() => {
    const map: Record<string, Physician> = {};
    physicians.forEach(p => {
      if (p.bracelet?.id) {
        map[p.bracelet.id] = p;
      }
    });
    return map;
  }, [physicians]);

  // Map beacon IDs to their assigned anesthesiologists (by bracelet.id)
  const beaconToAnesthesiologistMap = useMemo(() => {
    const map: Record<string, Anesthesiologist> = {};
    anesthesiologists.forEach(a => {
      if (a.bracelet?.id) {
        map[a.bracelet.id] = a;
      }
    });
    return map;
  }, [anesthesiologists]);

  return {
    rooms,
    existingGateways,
    existingBracelets,
    physicians,
    procedureTypes,
    operationRooms,
    patients,
    staff,
    anesthesiologists,
    roomsByType,
    existingTestGateways,
    existingTestBracelets,
    patientsWithTestBracelets,
    beaconToPatientMap,
    beaconToStaffMap,
    beaconToPhysicianMap,
    beaconToAnesthesiologistMap,
    isLoading: roomsLoading || gatewaysLoading,
    editGateway,
    createPatient,
    editPatient,
    editStaff,
    editPhysician,
    editAnesthesiologist,
    refetchGateways,
    refetchPatients,
    refetchStaff,
    refetchPhysicians,
    refetchAnesthesiologists,
  };
};
